import { useState, useEffect } from 'react';
import HeroSection from './components/HeroSection';
import SolutionsSection from './components/SolutionsSection';
import AidesSection from './components/AidesSection';
import ProfessionalSection from './components/ProfessionalSection';
import WhyChooseSection from './components/WhyChooseSection';
import InterventionZoneSection from './components/InterventionZoneSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import PromoPopup from '../../components/feature/PromoPopup';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SchemaOrg from '../../components/feature/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['Organization', 'LocalBusiness'],
      '@id': `${siteUrl}/#organization`,
      name: 'AIRPURE',
      url: siteUrl,
      telephone: '+33189293323',
      description:
        'Expert RGE en pompe à chaleur, panneaux photovoltaïques et isolation en France. Aides MaPrimeRénov\' et CEE jusqu\'à 15 000 €. Devis gratuit et financement Sofinco.',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'FR',
      },
      areaServed: { '@type': 'Country', name: 'France' },
      priceRange: '€€',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Solutions énergétiques durables',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Pompe à chaleur air/air', description: 'Installation PAC air/air avec jusqu\'à 5 000 € d\'aides MaPrimeRénov\'' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Pompe à chaleur air/eau', description: 'Installation PAC air/eau avec jusqu\'à 10 000 € d\'aides MaPrimeRénov\'' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Panneaux photovoltaïques', description: 'Installation de panneaux solaires photovoltaïques avec TVA réduite' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Système solaire combiné', description: 'Système solaire combiné avec jusqu\'à 15 000 € d\'aides' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Isolation des combles', description: 'Isolation combles avec jusqu\'à 7 000 € d\'aides CEE et MaPrimeRénov\'' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Isolation extérieure ITE', description: 'Isolation thermique par l\'extérieur avec jusqu\'à 15 000 € d\'aides' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Isolation sous-sol', description: 'Isolation sous-sol et vide sanitaire avec jusqu\'à 5 000 € d\'aides' },
          },
        ],
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'AIRPURE',
      publisher: { '@id': `${siteUrl}/#organization` },
      inLanguage: 'fr-FR',
    },
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: siteUrl,
      name: 'AIRPURE France - Pompe à Chaleur, Photovoltaïque & Isolation | MaPrimeRénov\'',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      description:
        'AIRPURE, expert RGE en pompe à chaleur, panneaux photovoltaïques et isolation en France. Aides MaPrimeRénov\' et CEE jusqu\'à 15 000 €. Devis gratuit, financement Sofinco.',
      inLanguage: 'fr-FR',
      dateModified: '2026-04-12',
    },
    {
      '@type': 'FAQPage',
      '@id': `${siteUrl}/#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Quelles aides puis-je obtenir pour une pompe à chaleur ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pour une pompe à chaleur air/air, vous pouvez obtenir jusqu\'à 5 000 € via MaPrimeRénov\'. Pour une PAC air/eau, jusqu\'à 10 000 €. Ces aides sont cumulables avec les Certificats d\'Économies d\'Énergie (CEE) et la TVA à taux réduit.',
          },
        },
        {
          '@type': 'Question',
          name: 'AIRPURE est-il certifié RGE ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Oui, AIRPURE est certifié RGE (Reconnu Garant de l\'Environnement), Qualibat et qualifié MaPrimeRénov\'. Cette certification est indispensable pour que vous puissiez bénéficier des aides de l\'État.',
          },
        },
        {
          '@type': 'Question',
          name: 'Puis-je financer mes travaux avec AIRPURE ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Oui, AIRPURE propose un financement via son partenaire Sofinco, vous permettant d\'étaler le coût de vos travaux tout en bénéficiant des aides MaPrimeRénov\' et CEE.',
          },
        },
        {
          '@type': 'Question',
          name: 'Quelles aides pour l\'isolation de ma maison ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pour l\'isolation des combles, vous pouvez obtenir jusqu\'à 7 000 €. Pour l\'isolation extérieure (ITE), jusqu\'à 15 000 €. Pour l\'isolation sous-sol/vide sanitaire, jusqu\'à 5 000 €. Ces montants peuvent être cumulés avec les CEE.',
          },
        },
      ],
    },
  ],
};

export default function Home() {
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  useEffect(() => {
    document.title =
      'AIRPURE France - Pompe à Chaleur, Photovoltaïque & Isolation | MaPrimeRénov\'';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'AIRPURE, expert RGE en pompe à chaleur, panneaux photovoltaïques et isolation en France. Aides MaPrimeRénov\' et CEE jusqu\'à 15 000 €. Devis gratuit, financement Sofinco disponible.',
      );
    }

    const timer = setTimeout(() => {
      setShowPromoPopup(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg schema={homeSchema} id="schema-home" />
      <HeroSection />
      <SolutionsSection />
      <AidesSection />
      <ProfessionalSection />
      <WhyChooseSection />
      <InterventionZoneSection />
      <ContactSection />
      <Footer />

      <PromoPopup
        isOpen={showPromoPopup}
        onClose={() => setShowPromoPopup(false)}
      />

      <WhatsAppButton />
    </div>
  );
}
